import { useQuery } from '@tanstack/react-query';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import dataService from 'services/dataService';
import { API_GOALS } from 'services/dataService/ccmResources';
import { showResult } from 'infrastructure/hooks/utils';

import type { SortState } from 'infrastructure/types';
import type {
  IPartialPaginationMeta,
  IPatientGoalResponse,
  TPatientGoalType,
} from 'infrastructure/interfaces';

type TFilter = {
  patientGuid: string;
  type: TPatientGoalType;
  documentationTimeSort?: SortState;
} & IPartialPaginationMeta;

type TDataProps = Omit<TFilter, 'documentationTimeSort'> & {
  documentationTimeSort?: string;
};

export const getPatientGoalsFn = async ({ ...filter }: TDataProps) => {
  const url = API_GOALS;
  const { data, error } = await dataService.getList<IPatientGoalResponse>(
    apiUrlGenerator(url, filter),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const getPatientGoalsKey = (filter: TDataProps) => [
  'getPatientGoals',
  filter,
];

export const PATIENT_GOALS_PER_PAGE = 5;

export const useGetPatientGoals = (props: TFilter) => {
  const filter: TDataProps = {
    ...props,
    items: PATIENT_GOALS_PER_PAGE,
    documentationTimeSort: props.documentationTimeSort?.toUpperCase(),
  };

  const {
    data: goals,
    isLoading,
    isFetching,
    isRefetching,
    ...other
  } = useQuery({
    queryKey: getPatientGoalsKey(filter),
    queryFn: async () => getPatientGoalsFn(filter),
  });

  return {
    goals,
    isLoading: isLoading || isFetching || isRefetching,
    isFetching,
    ...other,
  };
};
